import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { MaskedLine, FadeUp, Grow } from "../components/motion";
import FestivalBanner from "../components/FestivalBanner";
import { METROS } from "../data/metros";
import { Link } from "react-router-dom";
import { ArrowRight, X, Check, Phone, MessageCircle } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { Reveal, Eyebrow } from "../components/Reveal";
import { FinalCta } from "../components/Sections";
import {
    SITE,
    SERVICES,
    DONT_DO,
    WE_DO,
    BUSINESS_TYPES,
    MARKETS,
    PROCESS,
    DIAGNOSTIC_QUESTIONS,
    FAQS,
    EXPERIENCE_BRANDS,
} from "../data/site";

const FIELD_PHOTOS = METROS.filter((m) => m.photos).flatMap((m) => m.photos);

const EditorialMarquee = () => {
    const words = ["BRAND", "MARKETING", "SALES", "GROWTH"];
    const row = (key) => (
        <div key={key} className="flex items-center shrink-0">
            {[0, 1, 2].flatMap((r) =>
                words.map((w, i) => (
                    <span key={`${r}-${w}`} className="flex items-center shrink-0">
                        <span
                            className={`mx-8 font-display text-5xl sm:text-7xl font-extrabold tracking-tight whitespace-nowrap ${
                                i % 2 ? "text-outline" : "text-white"
                            }`}
                        >
                            {w}
                        </span>
                        <span className="h-2.5 w-2.5 bg-[#C5A059] shrink-0" />
                    </span>
                ))
            )}
        </div>
    );
    return (
        <section aria-hidden="true" className="bg-[#0D131F] border-y border-[#C5A059]/20 py-7 overflow-hidden select-none">
            <div className="marquee-track">{[row("a"), row("b")]}</div>
        </section>
    );
};

const HeroHome = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yGhost = useTransform(scrollYProgress, [0, 1], [0, 160]);
    const yContent = useTransform(scrollYProgress, [0, 1], [0, 70]);
    const opacityContent = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 55, damping: 18 });
    const sy = useSpring(my, { stiffness: 55, damping: 18 });
    const sx2 = useTransform(sx, (v) => v * -1.5);
    const sy2 = useTransform(sy, (v) => v * -1.5);

    const onMouseMove = (e) => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        mx.set(((e.clientX - r.left) / r.width - 0.5) * 34);
        my.set(((e.clientY - r.top) / r.height - 0.5) * 22);
    };

    return (
        <section
            ref={ref}
            onMouseMove={onMouseMove}
            className="relative bg-[#0D131F] hero-grid text-white pt-36 sm:pt-48 pb-24 sm:pb-28 overflow-hidden"
        >
            <div className="hero-noise" />
            <motion.span
                aria-hidden="true"
                style={{ y: yGhost }}
                className="pointer-events-none select-none absolute -right-8 top-10 font-display font-extrabold text-[24vw] leading-none text-outline opacity-[0.12]"
            >
                ALBL
            </motion.span>
            <motion.span aria-hidden="true" style={{ x: sx, y: sy }} className="absolute left-[6%] top-[24%] hidden md:block h-4 w-4 bg-[#C5A059]" />
            <motion.span aria-hidden="true" style={{ x: sx2, y: sy2 }} className="absolute left-[10%] top-[30%] hidden md:block h-16 w-16 border border-[#C5A059]/40 rotate-12" />

            <motion.div
                style={{ y: yContent, opacity: opacityContent }}
                className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-12 gap-14 items-center"
            >
                <div className="lg:col-span-7">
                    <FadeUp y={14}>
                        <Eyebrow dark>Brand. Marketing. Sales. Growth.</Eyebrow>
                    </FadeUp>
                    <h1
                        className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.04] mt-7"
                        data-testid="hero-heading"
                    >
                        <MaskedLine delay={0.15}>WE DON&apos;T JUST</MaskedLine>
                        <MaskedLine delay={0.28}>MARKET BUSINESSES.</MaskedLine>
                        <MaskedLine delay={0.48} className="text-[#C5A059]">WE BUILD BUSINESSES</MaskedLine>
                        <MaskedLine delay={0.62} className="text-[#C5A059]">THAT SELL.</MaskedLine>
                    </h1>
                    <FadeUp delay={0.9}>
                        <p
                            className="mt-7 font-serif-accent italic text-lg sm:text-2xl text-slate-300"
                            data-testid="hero-subheading"
                        >
                            Big-brand marketing thinking for ambitious businesses.
                        </p>
                        <p className="mt-4 text-sm sm:text-base text-slate-400 tracking-wide max-w-xl">
                            Brand strategy. Market execution. Digital growth. Customer acquisition.
                            Sales-focused marketing.
                        </p>
                    </FadeUp>
                    <FadeUp delay={1.05}>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link
                                to="/contact"
                                data-testid="hero-cta-talk-button"
                                className="btn-sharp bg-white text-[#0D131F] text-xs font-bold tracking-[0.15em] uppercase px-8 py-4"
                            >
                                Talk to ALBL
                            </Link>
                            <Link
                                to="/contact?intent=audit"
                                data-testid="hero-cta-audit-button"
                                className="btn-sharp border border-[#C5A059] text-[#C5A059] text-xs font-bold tracking-[0.15em] uppercase px-8 py-4 hover:bg-[#C5A059] hover:text-[#0D131F]"
                            >
                                Get a Growth Audit
                            </Link>
                            <a
                                href={SITE.whatsappPrefill}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="hero-whatsapp-button"
                                className="btn-sharp flex items-center gap-2 bg-[#25D366] text-[#0D131F] text-xs font-bold tracking-[0.15em] uppercase px-8 py-4"
                            >
                                <MessageCircle className="h-4 w-4" /> WhatsApp
                            </a>
                        </div>
                        <a
                            href={SITE.phoneHref}
                            className="mt-8 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
                            data-testid="hero-phone-link"
                        >
                            <Phone className="h-4 w-4 text-[#C5A059]" /> Call {SITE.phone}
                        </a>
                    </FadeUp>
                </div>
                <div className="lg:col-span-5 relative">
                    <FadeUp delay={0.55} y={48}>
                        <div className="relative border border-white/10 bg-white/[0.03] backdrop-blur-sm p-8 sm:p-10">
                            <span className="absolute -top-1.5 -left-1.5 h-3 w-3 bg-[#C5A059]" />
                            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#C5A059]">
                                We start with the business
                            </p>
                            <ul className="mt-6 space-y-5">
                                {DIAGNOSTIC_QUESTIONS.map((q, i) => (
                                    <li key={q} className="flex gap-4 items-baseline group">
                                        <span className="font-serif-accent italic text-[#C5A059] text-lg shrink-0">
                                            0{i + 1}
                                        </span>
                                        <span className="text-slate-200 text-sm sm:text-base group-hover:translate-x-1.5 transition-transform duration-300">
                                            {q}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-8 pt-6 border-t border-white/10 text-sm text-slate-400 leading-relaxed">
                                Then we build the marketing around those answers.
                            </p>
                        </div>
                    </FadeUp>
                </div>
            </motion.div>
            <FadeUp delay={1.4} className="relative mt-16 flex flex-col items-center gap-3">
                <div className="scroll-cue-line" />
                <span className="text-[10px] tracking-[0.35em] uppercase text-slate-500">Scroll</span>
            </FadeUp>
        </section>
    );
};

const Home = () => {
    useEffect(() => {
        document.title = "ALBL Communications | Brand & Sales Growth Agency in Narmadapuram";
    }, []);

    return (
        <main>
            {/* HERO */}
            <HeroHome />

            {/* EDITORIAL MARQUEE */}
            <EditorialMarquee />

            {/* FESTIVAL SEASON CAMPAIGN */}
            <FestivalBanner />

            {/* CHAPTER 01 — THE MANIFESTO */}
            <section className="bg-[#F8FAFC] relative overflow-hidden" data-testid="differentiation-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-24 sm:py-36">
                    <FadeUp y={18}>
                        <div className="flex items-center gap-6">
                            <span className="font-serif-accent italic text-6xl sm:text-8xl text-[#C5A059] leading-none">01</span>
                            <div className="h-px flex-1 bg-slate-300" />
                            <Eyebrow>The manifesto</Eyebrow>
                        </div>
                    </FadeUp>
                    <h2
                        className="font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.06] mt-10 max-w-5xl"
                        data-testid="differentiation-heading"
                    >
                        <MaskedLine scroll delay={0.05}>YOU DON&apos;T NEED</MaskedLine>
                        <MaskedLine scroll delay={0.15}>ANOTHER MARKETING</MaskedLine>
                        <MaskedLine scroll delay={0.25}>
                            <span className="text-transparent" style={{ WebkitTextStroke: "1.5px #0D131F" }}>
                                AGENCY.
                            </span>
                        </MaskedLine>
                    </h2>
                    <div className="mt-14 grid lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <FadeUp delay={0.1}>
                                <p className="font-serif-accent italic text-xl sm:text-2xl text-slate-800 leading-relaxed">
                                    Most agencies sell you posts, reels, websites and &quot;digital
                                    marketing&quot;.
                                </p>
                                <p className="mt-5 text-slate-900 text-base sm:text-lg font-semibold">
                                    ALBL starts somewhere else.
                                </p>
                                <Grow delay={0.25} className="mt-8">
                                    <div className="gold-rule" />
                                </Grow>
                                <p className="mt-10 text-slate-600 text-base sm:text-lg leading-relaxed">
                                    We answer those questions first.{" "}
                                    <span className="text-slate-900 font-semibold">
                                        Then we build the marketing around the business.
                                    </span>
                                </p>
                            </FadeUp>
                        </div>
                        <div className="lg:col-span-7">
                            {DIAGNOSTIC_QUESTIONS.map((q, i) => (
                                <FadeUp key={q} delay={i * 0.07} y={22}>
                                    <div className="group flex items-baseline gap-6 border-b border-slate-200 py-5 hover:border-[#0D131F] transition-colors duration-300">
                                        <span className="font-serif-accent italic text-2xl text-[#C5A059] shrink-0">
                                            0{i + 1}
                                        </span>
                                        <span className="text-lg sm:text-2xl font-display font-semibold text-slate-900 group-hover:translate-x-2 transition-transform duration-300">
                                            {q}
                                        </span>
                                    </div>
                                </FadeUp>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* BIG-BRAND EXPERIENCE */}
            <section className="bg-[#0D131F] text-white" data-testid="experience-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28">
                    <Reveal>
                        <Eyebrow dark>Credentials</Eyebrow>
                        <h2
                            className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1] mt-5 max-w-3xl"
                            data-testid="credentials-heading"
                        >
                            BIG-BRAND EXPERIENCE.
                            <span className="block text-[#C5A059]">LOCAL-MARKET UNDERSTANDING.</span>
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-7">
                            <Reveal delay={100}>
                                <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
                                    ALBL is built on professional brand-marketing experience across
                                    leading brands including{" "}
                                    <span className="text-white font-semibold">LG, Haier and Bisleri</span>.
                                </p>
                                <p className="mt-5 text-slate-400 text-base leading-relaxed">
                                    We understand what happens behind a serious brand — from campaign
                                    strategy and consumer communication to retail visibility, trade
                                    marketing, digital campaigns and market execution.
                                </p>
                                <p className="mt-5 text-slate-300 text-base sm:text-lg leading-relaxed">
                                    Now we&apos;re bringing that thinking to ambitious businesses in
                                    Narmadapuram, Itarsi and surrounding markets.
                                </p>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-5">
                            <Reveal delay={200}>
                                <div className="grid grid-cols-3 gap-3">
                                    {EXPERIENCE_BRANDS.map((b) => (
                                        <div
                                            key={b}
                                            data-testid={`credential-brand-${b.toLowerCase()}`}
                                            className="border border-white/10 bg-white/[0.03] py-8 flex items-center justify-center font-display text-2xl font-bold tracking-widest text-slate-200"
                                        >
                                            {b}
                                        </div>
                                    ))}
                                </div>
                                <p className="mt-5 text-xs text-slate-500 leading-relaxed">
                                    Brand names indicate professional brand-marketing experience only.
                                    ALBL is not an official partner, representative or agency of these
                                    companies.
                                </p>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="bg-white" data-testid="services-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28">
                    <Reveal>
                        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                            <div>
                                <Eyebrow>What we do</Eyebrow>
                                <h2
                                    className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mt-5"
                                    data-testid="services-heading"
                                >
                                    FROM BRAND THINKING TO SALES EXECUTION.
                                </h2>
                            </div>
                            <Link
                                to="/services"
                                data-testid="services-view-all-link"
                                className="btn-sharp inline-flex items-center gap-2 text-xs font-bold tracking-[0.15em] uppercase text-[#1E3A8A] shrink-0"
                            >
                                All services <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </Reveal>
                    <div className="mt-14 grid gap-px bg-slate-200 border border-slate-200">
                        {SERVICES.map((s, i) => (
                            <Reveal key={s.slug} delay={i * 60}>
                                <Link
                                    to={`/services#${s.slug}`}
                                    data-testid={`service-card-${i + 1}`}
                                    className="group grid sm:grid-cols-12 gap-4 sm:gap-8 items-baseline bg-white lift px-6 sm:px-10 py-8"
                                >
                                    <span className="font-serif-accent italic text-2xl text-[#C5A059] sm:col-span-1">
                                        {s.num}
                                    </span>
                                    <h3 className="font-display text-xl sm:text-2xl font-bold text-slate-900 uppercase tracking-tight sm:col-span-4">
                                        {s.title}
                                    </h3>
                                    <p className="text-slate-600 text-sm sm:text-base leading-relaxed sm:col-span-6">
                                        {s.short}
                                    </p>
                                    <ArrowRight className="hidden sm:block h-5 w-5 text-slate-300 group-hover:text-[#C5A059] group-hover:translate-x-1 transition-all sm:col-span-1 justify-self-end" />
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CHAPTER 02 — HOUSE RULES */}
            <section className="bg-[#0D131F] text-white" data-testid="do-dont-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28">
                    <FadeUp y={18}>
                        <div className="flex items-center gap-6 mb-14">
                            <span className="font-serif-accent italic text-6xl sm:text-8xl text-[#C5A059] leading-none">02</span>
                            <div className="h-px flex-1 bg-white/15" />
                            <Eyebrow dark>House rules</Eyebrow>
                        </div>
                    </FadeUp>
                    <div className="grid lg:grid-cols-2 gap-14">
                    <Reveal>
                        <h2
                            className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.1]"
                            data-testid="do-dont-heading"
                        >
                            WHAT WE DON&apos;T DO.
                        </h2>
                        <ul className="mt-10 space-y-5" data-testid="dont-list-container">
                            {DONT_DO.map((item) => (
                                <li key={item} className="flex gap-4 items-start">
                                    <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center border border-red-400/50">
                                        <X className="h-3 w-3 text-red-400" />
                                    </span>
                                    <span className="text-slate-300 text-sm sm:text-base uppercase tracking-wide">
                                        {item}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                    <Reveal delay={150}>
                        <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.1] text-[#C5A059]">
                            WHAT WE DO.
                        </h2>
                        <ul className="mt-10 space-y-5" data-testid="do-list-container">
                            {WE_DO.map((item) => (
                                <li key={item} className="flex gap-4 items-start">
                                    <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center border border-[#C5A059]/60">
                                        <Check className="h-3 w-3 text-[#C5A059]" />
                                    </span>
                                    <span className="text-slate-200 text-sm sm:text-base">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                    </div>
                </div>
            </section>

            {/* WHO WE WORK WITH */}
            <section className="bg-[#F8FAFC]" data-testid="who-we-work-with-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28">
                    <Reveal>
                        <Eyebrow>Who we work with</Eyebrow>
                        <h2
                            className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mt-5 max-w-3xl"
                            data-testid="who-we-work-with-heading"
                        >
                            BUILT FOR BUSINESSES THAT WANT TO GROW.
                        </h2>
                    </Reveal>
                    <Reveal delay={100}>
                        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
                            {BUSINESS_TYPES.map((t) => (
                                <div
                                    key={t}
                                    className="bg-white px-5 py-6 text-sm font-medium text-slate-700 hover:bg-[#0D131F] hover:text-white transition-colors duration-300"
                                >
                                    {t}
                                </div>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal delay={180}>
                        <p className="mt-10 text-slate-600 text-base sm:text-lg leading-relaxed max-w-3xl">
                            Whether you&apos;re launching, rebuilding your brand, entering a new market
                            or simply trying to sell more, ALBL builds marketing around where your
                            business actually stands.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* FIELD NOTES */}
            <section className="bg-white" data-testid="field-notes-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28">
                    <FadeUp y={18}>
                        <div className="flex items-center gap-6">
                            <Eyebrow>Field notes</Eyebrow>
                            <div className="h-px flex-1 bg-slate-200" />
                            <span className="font-serif-accent italic text-slate-400 text-sm sm:text-base">
                                on the ground in our markets
                            </span>
                        </div>
                    </FadeUp>
                    <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                        {FIELD_PHOTOS.map((p, i) => (
                            <FadeUp key={p.src} delay={i * 0.06} y={28}>
                                <figure className="clip-frame border border-slate-200 bg-[#0D131F] overflow-hidden">
                                    <img
                                        src={p.src}
                                        alt={p.alt}
                                        loading="lazy"
                                        className="photo-treatment w-full h-48 sm:h-64 object-cover"
                                    />
                                    <figcaption className="px-4 py-3 text-xs text-slate-400 font-serif-accent italic">
                                        {p.caption}
                                    </figcaption>
                                </figure>
                            </FadeUp>
                        ))}
                    </div>
                    <p className="mt-5 text-xs text-slate-400">
                        Photos: Wikimedia Commons contributors, CC-licensed.
                    </p>
                </div>
            </section>

            {/* LOCAL MARKETS */}
            <section className="bg-[#0D131F] hero-grid text-white" data-testid="markets-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28">
                    <Reveal>
                        <Eyebrow dark>Our markets</Eyebrow>
                        <h2
                            className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1] mt-5"
                            data-testid="local-markets-heading"
                        >
                            STARTING LOCAL. <span className="text-[#C5A059]">THINKING BIG.</span>
                        </h2>
                        <p className="mt-6 text-slate-400 text-base sm:text-lg leading-relaxed max-w-2xl">
                            ALBL works with businesses across Narmadapuram, Itarsi and surrounding
                            Central Madhya Pradesh markets.
                        </p>
                    </Reveal>
                    <Reveal delay={120}>
                        <div className="mt-12 flex flex-wrap gap-3">
                            {MARKETS.map((m) => (
                                <span
                                    key={m.name}
                                    data-testid={`market-chip-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`px-5 py-3 text-sm tracking-wide border ${
                                        m.primary
                                            ? "border-[#C5A059] text-[#C5A059] font-semibold"
                                            : "border-white/15 text-slate-300"
                                    }`}
                                >
                                    {m.name}
                                </span>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* PROCESS */}
            <section className="bg-white" data-testid="process-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28">
                    <Reveal>
                        <Eyebrow>How we work</Eyebrow>
                        <h2
                            className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mt-5"
                            data-testid="process-heading"
                        >
                            NO COOKIE-CUTTER MARKETING.
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
                        {PROCESS.map((p, i) => (
                            <Reveal key={p.num} delay={i * 80}>
                                <div
                                    className="bg-white p-8 h-full hover:bg-[#F8FAFC] transition-colors"
                                    data-testid={`process-step-${i + 1}`}
                                >
                                    <span className="font-serif-accent italic text-3xl text-[#C5A059]">
                                        {p.num}
                                    </span>
                                    <h3 className="font-display text-xl font-bold text-slate-900 uppercase tracking-tight mt-4">
                                        {p.title}
                                    </h3>
                                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{p.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY ALBL EXISTS */}
            <section className="bg-[#F8FAFC]" data-testid="about-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <Eyebrow>About ALBL</Eyebrow>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mt-5">
                                WHY ALBL EXISTS.
                            </h2>
                            <div className="gold-rule mt-8" />
                            <Link
                                to="/about"
                                data-testid="about-read-more-link"
                                className="btn-sharp mt-10 inline-flex items-center gap-2 text-xs font-bold tracking-[0.15em] uppercase text-[#1E3A8A]"
                            >
                                More about ALBL <ArrowRight className="h-4 w-4" />
                            </Link>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-7">
                        <Reveal delay={120}>
                            <p className="font-serif-accent italic text-xl sm:text-2xl text-slate-800 leading-relaxed">
                                Local businesses shouldn&apos;t have to choose between expensive
                                corporate agencies and generic digital marketing packages.
                            </p>
                            <p className="mt-6 text-slate-600 text-base sm:text-lg leading-relaxed">
                                They deserve access to serious marketing thinking — adapted to their
                                market, their customers and their business reality.
                            </p>
                            <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
                                With professional experience across brands such as LG, Haier and
                                Bisleri, ALBL brings structured brand-marketing thinking to businesses
                                that want to grow.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="bg-white" data-testid="faq-section">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28">
                    <Reveal>
                        <Eyebrow>Questions</Eyebrow>
                        <h2
                            className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mt-5"
                            data-testid="faq-heading"
                        >
                            FREQUENTLY ASKED QUESTIONS.
                        </h2>
                    </Reveal>
                    <Reveal delay={120}>
                        <Accordion type="single" collapsible className="mt-12">
                            {FAQS.map((f, i) => (
                                <AccordionItem key={f.q} value={`faq-${i}`} data-testid={`faq-item-${i + 1}`}>
                                    <AccordionTrigger
                                        className="text-left font-display text-base sm:text-lg font-semibold text-slate-900 hover:text-[#1E3A8A]"
                                        data-testid={`faq-trigger-${i + 1}`}
                                    >
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-slate-600 text-sm sm:text-base leading-relaxed">
                                        {f.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Reveal>
                </div>
            </section>

            <FinalCta />
        </main>
    );
};

export default Home;
